import React, { useEffect } from 'react'
import { useIntl } from 'react-intl'
import {
  useRecoilCallback,
  useRecoilState,
  useRecoilValue,
  useSetRecoilState
} from 'recoil'
import {
  Button,
  BUTTON_VARIANT_PRIMARY,
  BUTTON_VARIANT_SECONDARY,
  Flex,
  FlexItem
} from 'mailup-web-components'

import {
  manageButtonsState,
  progressState
} from 'Pages/EmailSendingWizard/recoil/state/queue'

import {
  BEQUEUE_CONFIG_STATE_FAILED,
  BEQUEUE_CONFIG_STATE_RUNNING
} from 'Api/beQueueApi'
import evalCondition from 'Helpers/evalCondition'
import { modalDeleteState } from 'Pages/EmailSendingWizard/recoil/state/modals'
import {
  retryCallback,
  scheduleNowCallback,
  sendingScheduleSetImmediateCallback,
  sendingScheduleSetScheduleCallback,
  setFasciaDiNonDisturboCallback
} from 'Pages/EmailSendingWizard/recoil/callbacks/callback'
import { useHistory } from 'react-router-dom'
import {
  isScheduledSendingState,
  senderInfoState
} from 'Pages/EmailSendingWizard/recoil/state/senderInfo'
import { storeGet } from 'Helpers/localStorage'
import { STORE_KEY_ACCOUNT, SENDING_SOURCE_IMMEDIATE } from 'Constants/'
import { fasciaDiNonDisturboInvalidSelector } from 'Pages/EmailSendingWizard/recoil/state/fasciaDiNonDisturbo'
import {
  contentScheduledState,
  isValidDateState,
  setBoxErrorState
} from 'Components/ContentScheduled/recoil/state'
import { verifyDateCallback } from 'Components/ContentScheduled/recoil/callbacks'

import { MODE_IMMEDIATE, MODE_SCHEDULED, MODE_SMART } from 'Constants/index'

import { pageState } from 'Pages/EmailSendingWizard/recoil/state/page'

const BoxSendingManage = () => {
  const intl = useIntl()
  const history = useHistory()

  const sendNowVisibility = evalCondition(
    'NOT uo:NOSEND AND NOT uo:NOSCHED AND NOT uo:NOIMMEDIATESEND'
  )
  const scheduleVisibility = evalCondition(
    'NOT uo:NOSEND AND (NOT uo:NOSCHED OR NOT uo:NOIMMEDIATESEND)'
  )

  const { isScheduledSending, scheduleModeSelected } = useRecoilValue(
    isScheduledSendingState
  )
  const isValidDate = useRecoilValue(isValidDateState)

  const standbyVisibility = evalCondition('uo:SEND')
  const { queueStatus, queued } = useRecoilValue(progressState)
  const { sendingOrScheduleId, pecPlusStatus } = useRecoilValue(senderInfoState)
  const [{ retryDisabled, deleteDisabled }, setButtonsState] = useRecoilState(
    manageButtonsState
  )
  const { GracePeriod: gracePeriod } = storeGet(STORE_KEY_ACCOUNT)
  const openModalDelete = useSetRecoilState(modalDeleteState)
  const retry = useRecoilCallback(retryCallback, [])
  const scheduleNow = useRecoilCallback(scheduleNowCallback, [])
  const fasciaDiNonDisturboInvalid = useRecoilValue(
    fasciaDiNonDisturboInvalidSelector
  )
  const setFasciaDiNonDisturbo = useRecoilCallback(
    setFasciaDiNonDisturboCallback,
    []
  )
  const { momentUtcDate, momentTimeZoneDate, timeZone } = useRecoilValue(
    contentScheduledState
  )
  const verifyDate = useRecoilCallback(verifyDateCallback, [])
  const setBoxError = useSetRecoilState(setBoxErrorState)
  const sendingScheduleSetImmediate = useRecoilCallback(
    sendingScheduleSetImmediateCallback,
    []
  )
  const sendingScheduleSetSchedule = useRecoilCallback(
    sendingScheduleSetScheduleCallback,
    []
  )
  const setPageState = useSetRecoilState(pageState)

  useEffect(() => {}, [])

  const handleOnRetryClick = () => {
    setButtonsState(prev => ({
      ...prev,
      retryDisabled: true,
      deleteDisabled: true
    }))
    retry().then(e => {
      setButtonsState(prev => ({
        ...prev,
        retryDisabled: false,
        deleteDisabled: false
      }))
    })
  }

  const handleOnDeleteClick = () => {
    setButtonsState(prev => ({
      ...prev,
      retryDisabled: true,
      deleteDisabled: true
    }))
    openModalDelete(prev => ({
      ...prev,
      show: true
    }))
  }

  const handleOnStandByClick = () => {
    setFasciaDiNonDisturbo('standBy').then(a => {
      if (a) {
        history.push('/messages/email/sendstatus/campaigns')
      }
    })
  }

  const handleOnSendNowClick = () => {
    setFasciaDiNonDisturbo('SendNow').then(a => {
      scheduleNow(sendingOrScheduleId, SENDING_SOURCE_IMMEDIATE).then(e => {
        if (a) {
          history.push('/messages/email/sendstatus/campaigns')
        }
      })
    })
  }

  return (
    <Flex justifyContent='flex-end' columnGap='8px'>
      {queueStatus === BEQUEUE_CONFIG_STATE_FAILED && (
        <FlexItem>
          <Button
            kind={BUTTON_VARIANT_SECONDARY}
            disabled={retryDisabled}
            onClick={handleOnRetryClick}
            data-mu-test='EmailSendingWizard-RetryQueue'
          >
            {intl.formatMessage({ id: 'Load.EmailSendingWizard.RetryQueue' })}
          </Button>
        </FlexItem>
      )}

      {queueStatus === BEQUEUE_CONFIG_STATE_FAILED && (
        <FlexItem>
          <Button
            kind={BUTTON_VARIANT_PRIMARY}
            disabled={deleteDisabled}
            onClick={handleOnDeleteClick}
            data-mu-test='EmailSendingWizard-CancelQueue'
          >
            {intl.formatMessage({ id: 'Load.EmailSendingWizard.CancelQueue' })}
          </Button>
        </FlexItem>
      )}

      {standbyVisibility && queueStatus !== BEQUEUE_CONFIG_STATE_FAILED && (
        <FlexItem>
          <Button
            kind={BUTTON_VARIANT_SECONDARY}
            disabled={
              queueStatus === BEQUEUE_CONFIG_STATE_RUNNING ||
              queueStatus === BEQUEUE_CONFIG_STATE_FAILED ||
              fasciaDiNonDisturboInvalid
            }
            onClick={handleOnStandByClick}
            data-mu-test='EmailSendingWizard-ActionStandby'
          >
            {intl.formatMessage({ id: 'Load.QueueStatus.ActionStandby' })}
          </Button>
        </FlexItem>
      )}

      {scheduleVisibility &&
        queueStatus !== BEQUEUE_CONFIG_STATE_FAILED &&
        isScheduledSending && (
          <FlexItem>
            <Button
              kind={BUTTON_VARIANT_PRIMARY}
              disabled={
                queued === 0 ||
                queueStatus === BEQUEUE_CONFIG_STATE_RUNNING ||
                !isValidDate
              }
              onClick={async () => {
                if (
                  scheduleModeSelected === MODE_SCHEDULED ||
                  scheduleModeSelected === MODE_SMART
                ) {
                  const { valid } = await verifyDate(momentTimeZoneDate)
                  if (valid) {
                    setPageState(prev => ({
                      ...prev,
                      loading: true
                    }))

                    sendingScheduleSetSchedule(
                      momentUtcDate,
                      timeZone,
                      scheduleModeSelected
                    ).then(e => {
                      if (e) {
                        setFasciaDiNonDisturbo('scheduled').then(a => {
                          if (a) {
                            history.push('/messages/email/sendstatus/campaigns')
                          }
                        })
                      }
                    })
                  } else {
                    setPageState(prev => ({
                      ...prev,
                      loading: false
                    }))
                    setBoxError(true)
                  }
                }
                if (scheduleModeSelected === MODE_IMMEDIATE) {
                  sendingScheduleSetImmediate().then(e => {
                    if (e) {
                      setFasciaDiNonDisturbo('SendNow').then(a => {
                        if (a) {
                          history.push('/messages/email/sendstatus/campaigns')
                        }
                      })
                    }
                  })
                }
              }}
              data-mu-test='EmailSendingWizard-ActionPianifica'
            >
              {intl.formatMessage({ id: 'Load.QueueStatus.ActionPianifica' })}
            </Button>
          </FlexItem>
        )}
      {sendNowVisibility &&
        queueStatus !== BEQUEUE_CONFIG_STATE_FAILED &&
        !isScheduledSending && (
          <FlexItem>
            <Button
              disabled={
                (pecPlusStatus && pecPlusStatus !== 'Complete') ||
                queued === 0 ||
                queueStatus === BEQUEUE_CONFIG_STATE_FAILED ||
                queueStatus === BEQUEUE_CONFIG_STATE_RUNNING ||
                gracePeriod === 2 ||
                fasciaDiNonDisturboInvalid
              }
              kind={BUTTON_VARIANT_PRIMARY}
              data-mu-test='EmailSendingWizard-ActionImmediato'
              onClick={handleOnSendNowClick}
            >
              {intl.formatMessage({ id: 'Load.QueueStatus.ActionImmediato' })}
            </Button>
          </FlexItem>
        )}
    </Flex>
  )
}

export default BoxSendingManage
